import {
  Banner,
  Card,
  Disclosure,
  PageHeader,
  buttonVariants,
  cn,
} from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

import { displayError } from "../api/errors.js";
import { AttentionItem } from "../attention/attention-page.js";
import { describeFollowUp } from "../attention/attention-rows.js";
import { listFollowUps, resolveFollowUp } from "../attention/followups-api.js";
import { useAttentionLookups } from "../attention/use-attention-lookups.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { listPlanInstances } from "../plan/plan-api.js";
import { todayIn } from "../reports/report-period.js";
import { SetupChecklist } from "../setup/setup-checklist.js";
import { UpdateChecks } from "../setup/update-checks.js";
import { useSetupChecklist } from "../setup/use-setup-checklist.js";
import { useShell } from "../shell/shell-context.js";
import {
  listTaskDefinitions,
  listTaskOccurrences,
  type TaskOccurrence,
} from "../tasks/tasks-api.js";
import { dueLabel, unassignedSchedules } from "../tasks/tasks-view.js";
import {
  getLocationDashboard,
  getOrganisationOverview,
} from "./dashboard-api.js";
import { dateLabel } from "./local-day.js";
import {
  attentionPreview,
  checksHeadline,
  moreCounts,
  orderLocations,
  progressPercent,
  staffHeadline,
  summariseChecks,
  unassignedWarning,
  type TodayChecks,
} from "./today-summary.js";

// Dashboard data refreshes every five minutes; hidden tabs do not poll.
const POLL_MS = 300_000;
const MAKING_CHECKS_POLL_MS = 5_000;

function Progress({ done, total }: { done: number; total: number }) {
  return (
    <div
      aria-label={`${done} of ${total} done`}
      aria-valuemax={total}
      aria-valuemin={0}
      aria-valuenow={done}
      className="h-1.5 overflow-hidden rounded-full bg-stone-200"
      role="progressbar"
    >
      <div
        className="h-full rounded-full bg-emerald-800"
        style={{ width: `${progressPercent(done, total)}%` }}
      />
    </div>
  );
}

function Section({
  action,
  children,
  title,
}: {
  action?: ReactNode;
  children: ReactNode;
  title: string;
}) {
  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-stone-950">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function SeeAll({
  children,
  search,
  to,
}: {
  children: ReactNode;
  search?: Record<string, string>;
  to: "/attention" | "/tasks";
}) {
  return (
    <Link
      className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-800 hover:text-emerald-950"
      search={search ?? {}}
      to={to}
    >
      {children} <ArrowRight aria-hidden="true" className="size-4" />
    </Link>
  );
}

function CheckRows({
  items,
  timezone,
}: {
  items: readonly TaskOccurrence[];
  timezone: string;
}) {
  return (
    <Card className="overflow-hidden">
      <ul className="divide-y divide-stone-200">
        {items.map((item) => {
          const open = ["due", "missed", "upcoming"].includes(item.status);
          return (
            <li className="flex items-center gap-3 px-4 py-3" key={item.id}>
              <div className="min-w-0 flex-1">
                <p
                  className={cn(
                    "text-sm font-semibold",
                    !open && "text-stone-500",
                  )}
                >
                  {item.title}
                </p>
                <p
                  className={cn(
                    "mt-0.5 text-xs",
                    item.status === "missed"
                      ? "font-semibold text-red-800"
                      : "text-stone-500",
                  )}
                >
                  {item.status === "missed"
                    ? `Missed · ${dueLabel(item, timezone)}`
                    : item.status === "completed"
                      ? "Done"
                      : item.status === "skipped"
                        ? "Skipped"
                        : dueLabel(item, timezone)}
                </p>
              </div>
              {open && item.record_type_code ? (
                <Link
                  className={buttonVariants({ size: "sm", variant: "outline" })}
                  search={{
                    record_type_code: item.record_type_code,
                    task_occurrence_id: item.id,
                    ...(item.asset_id ? { asset_id: item.asset_id } : {}),
                  }}
                  to="/log"
                >
                  Log it
                </Link>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// Now first; what opens later and what is done stay folded with their counts.
function ChecksByTime({
  checks,
  timezone,
}: {
  checks: TodayChecks;
  timezone: string;
}) {
  if (checks.total === 0)
    return (
      <Card className="px-4 py-3 text-sm text-stone-600">No checks today</Card>
    );
  return (
    <div className="space-y-3">
      <Progress done={checks.done} total={checks.total} />
      {checks.now.length > 0 ? (
        <CheckRows items={checks.now} timezone={timezone} />
      ) : (
        <Card className="px-4 py-3 text-sm text-stone-600">
          {checks.later.length > 0 ? "Nothing due now" : "All done"}
        </Card>
      )}
      {checks.later.length > 0 ? (
        <Disclosure summary={`Later (${checks.later.length})`}>
          <CheckRows items={checks.later} timezone={timezone} />
        </Disclosure>
      ) : null}
      {checks.finished.length > 0 ? (
        <Disclosure summary={`Done (${checks.finished.length})`}>
          <CheckRows items={checks.finished} timezone={timezone} />
        </Disclosure>
      ) : null}
    </div>
  );
}

function StaffToday({
  locationId,
  locationName,
  timezone,
}: {
  locationId: string;
  locationName: string;
  timezone: string;
}) {
  const today = todayIn(timezone);
  const mine = useQuery({
    queryFn: () =>
      listTaskOccurrences(locationId, {
        assignee: "me",
        from: today,
        to: today,
      }),
    queryKey: ["dashboard", "my-checks", locationId, today],
    refetchInterval: POLL_MS,
  });
  const checks = summariseChecks(mine.data?.items ?? []);
  return (
    <div className="space-y-6">
      <PageHeader
        documentTitle="Today"
        eyebrow={dateLabel(today)}
        title={locationName}
      />
      {mine.isPending ? <LoadingState label="Loading your checks" /> : null}
      {mine.error ? (
        <ErrorState
          message={displayError(mine.error)}
          onAction={() => void mine.refetch()}
          title="Your checks aren’t available"
        />
      ) : null}
      {mine.data ? (
        <Section title={staffHeadline(checks.done, checks.total)}>
          <ChecksByTime checks={checks} timezone={timezone} />
        </Section>
      ) : null}
    </div>
  );
}

function NeedsAttention({
  hideWhenEmpty,
  locationId,
  timezone,
}: {
  hideWhenEmpty: boolean;
  locationId: string;
  timezone: string;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const followUps = useQuery({
    queryFn: () => listFollowUps(locationId, { status: "open" }),
    queryKey: ["dashboard", "follow-ups", locationId],
    refetchInterval: POLL_MS,
  });
  const items = followUps.data?.items ?? [];
  const preview = attentionPreview(items);
  const lookups = useAttentionLookups(locationId, preview.items);
  const close = useMutation({
    mutationFn: (id: string) => resolveFollowUp(id, { decision: "resolved" }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["follow-ups"] }),
      ]),
  });
  const total = followUps.data?.next_cursor
    ? `${items.length}+`
    : String(items.length);

  if (followUps.isPending) return <LoadingState label="Loading" />;
  if (hideWhenEmpty && items.length === 0 && !followUps.error) return null;
  if (followUps.error)
    return (
      <ErrorState
        message={displayError(followUps.error)}
        onAction={() => void followUps.refetch()}
        title="Attention isn’t available"
      />
    );

  return (
    <Section
      action={
        items.length > 0 ? <SeeAll to="/attention">All {total}</SeeAll> : null
      }
      title="Needs attention"
    >
      {items.length === 0 ? (
        <Card className="px-4 py-3 text-sm text-stone-600">
          Nothing needs attention
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="divide-y divide-stone-200">
            {preview.items.map((item) => (
              <AttentionItem
                canClose
                closing={close.isPending && close.variables === item.id}
                key={item.id}
                onClose={() => close.mutate(item.id)}
                onOpen={() =>
                  void navigate({ search: { item: item.id }, to: "/attention" })
                }
                row={describeFollowUp(item, lookups, timezone)}
              />
            ))}
            {preview.missed > 0 ? (
              <Link
                className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-stone-950 hover:bg-stone-50 sm:px-5"
                search={{ group: "missed_checks" }}
                to="/attention"
              >
                <span className="min-w-0 flex-1">
                  Missed checks{" "}
                  <span className="font-normal text-stone-500">
                    {preview.missed}
                    {followUps.data?.next_cursor ? "+" : ""}
                  </span>
                </span>
                <ChevronRight
                  aria-hidden="true"
                  className="size-4 shrink-0 text-stone-400"
                />
              </Link>
            ) : null}
          </div>
        </Card>
      )}
      {close.error ? (
        <p className="mt-2 text-sm font-semibold text-red-800" role="alert">
          {displayError(close.error)}
        </p>
      ) : null}
    </Section>
  );
}

function YourLocations({ activeId }: { activeId: string }) {
  const shell = useShell();
  const overview = useQuery({
    queryFn: getOrganisationOverview,
    queryKey: [
      "dashboard",
      "overview",
      shell.locations.map((entry) => entry.id),
    ],
    refetchInterval: POLL_MS,
  });
  if (overview.error)
    return (
      <ErrorState
        message={displayError(overview.error)}
        onAction={() => void overview.refetch()}
        title="Locations aren’t available"
      />
    );
  return (
    <Section title="Your locations">
      <Card className="overflow-hidden">
        <ul className="divide-y divide-stone-200">
          {orderLocations(overview.data?.locations ?? []).map((entry) => (
            <li key={entry.id}>
              <button
                aria-current={entry.id === activeId ? "true" : undefined}
                className={cn(
                  "flex w-full flex-col gap-1 px-4 py-3 text-left transition hover:bg-stone-50 sm:flex-row sm:items-center sm:gap-6",
                  entry.id === activeId && "bg-stone-50",
                )}
                onClick={() => shell.selectLocation(entry.id)}
                type="button"
              >
                <span className="min-w-0 flex-1 text-sm font-semibold">
                  {entry.name}
                </span>
                <span className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-500">
                  <span>
                    <span
                      className={cn(
                        "font-semibold text-stone-900",
                        entry.metrics.overdue.total > 0 && "text-amber-800",
                      )}
                    >
                      {entry.metrics.overdue.total}
                    </span>{" "}
                    missed
                  </span>
                  <span>
                    <span
                      className={cn(
                        "font-semibold text-stone-900",
                        entry.metrics.attention.total > 0 && "text-amber-800",
                      )}
                    >
                      {entry.metrics.attention.total}
                    </span>{" "}
                    need attention
                  </span>
                  <span>
                    <span className="font-semibold text-stone-900">
                      {entry.metrics.dueToday.total}
                    </span>{" "}
                    due today
                  </span>
                </span>
              </button>
            </li>
          ))}
          {overview.isPending ? (
            <li className="px-4 py-3 text-xs text-stone-500">Loading…</li>
          ) : null}
        </ul>
      </Card>
    </Section>
  );
}

export function DashboardPage() {
  const shell = useShell();
  const location = shell.activeLocation;
  const isManager = shell.activeRole !== "staff";
  const locationId = location?.id;
  const timezone = location?.timezone ?? "Pacific/Auckland";
  const today = todayIn(timezone);
  const managerReady = isManager && Boolean(locationId);
  const setup = useSetupChecklist();

  const metrics = useQuery({
    enabled: managerReady,
    queryFn: () => getLocationDashboard(locationId!),
    queryKey: ["dashboard", locationId],
    refetchInterval: POLL_MS,
  });
  const plans = useQuery({
    enabled: managerReady,
    queryFn: () => listPlanInstances(locationId!),
    queryKey: ["plan-instances", locationId],
  });
  const planPublished = (plans.data ?? []).some(
    (plan) => plan.state === "published",
  );
  const schedules = useQuery({
    enabled: managerReady,
    queryFn: () => listTaskDefinitions(locationId!),
    queryKey: ["dashboard", "schedules", locationId],
    // A plan published moments ago is still having its checks made.
    refetchInterval: (query) =>
      planPublished && query.state.data?.items.length === 0
        ? MAKING_CHECKS_POLL_MS
        : POLL_MS,
  });
  const todayChecks = useQuery({
    enabled: managerReady,
    queryFn: () =>
      listTaskOccurrences(locationId!, {
        assignee: "any",
        from: today,
        to: today,
      }),
    queryKey: ["dashboard", "checks", locationId, today],
    refetchInterval: (query) =>
      (schedules.data?.items.length ?? 0) > 0 &&
      query.state.data?.items.length === 0
        ? MAKING_CHECKS_POLL_MS
        : POLL_MS,
  });

  if (shell.locationsPending)
    return <LoadingState label="Loading your locations" />;
  if (shell.locationError)
    return (
      <ErrorState
        message={displayError(shell.locationError)}
        onAction={() => void shell.refreshLocations()}
        title="Your locations aren’t available"
      />
    );
  if (!location)
    return (
      <>
        <PageHeader title="Today" />
        <div className="mt-6">
          <EmptyState
            action={
              isManager ? (
                <Link
                  className={buttonVariants()}
                  search={{ add: "1", section: "locations" }}
                  to="/setup"
                >
                  Add a location
                </Link>
              ) : undefined
            }
            title="No locations yet"
          />
        </div>
      </>
    );
  if (!isManager)
    return (
      <StaffToday
        locationId={location.id}
        locationName={location.name}
        timezone={timezone}
      />
    );

  const checks = summariseChecks(todayChecks.data?.items ?? []);
  // While setup is unfinished its checklist already asks for this.
  const warning = setup.complete
    ? unassignedWarning(unassignedSchedules(schedules.data?.items ?? []).length)
    : null;

  return (
    <div className="space-y-7">
      <PageHeader
        documentTitle="Today"
        eyebrow={dateLabel(today)}
        title={location.name}
      />
      <SetupChecklist variant="embedded" />
      <UpdateChecks locationId={location.id} />
      {warning ? (
        <Banner
          action={
            <Link
              className={buttonVariants({ size: "sm", variant: "outline" })}
              search={{ section: "assign" }}
              to="/setup"
            >
              Assign people
            </Link>
          }
          title={warning}
          tone="attention"
        />
      ) : null}

      <NeedsAttention
        hideWhenEmpty={!setup.complete}
        locationId={location.id}
        timezone={timezone}
      />

      {setup.complete || checks.total > 0 ? (
        <Section
          action={<SeeAll to="/tasks">All checks</SeeAll>}
          title={checksHeadline(checks.done, checks.total)}
        >
          {todayChecks.isPending ? (
            <LoadingState label="Loading checks" />
          ) : null}
          {todayChecks.error ? (
            <ErrorState
              message={displayError(todayChecks.error)}
              onAction={() => void todayChecks.refetch()}
              title="Today’s checks aren’t available"
            />
          ) : null}
          {todayChecks.data ? (
            <ChecksByTime checks={checks} timezone={timezone} />
          ) : null}
        </Section>
      ) : null}

      {shell.locations.length > 1 ? (
        <YourLocations activeId={location.id} />
      ) : null}

      {metrics.data ? (
        <Disclosure summary="More counts">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {moreCounts(metrics.data).map((entry) => (
              <Link
                className="rounded-md border border-stone-200 bg-surface p-4 hover:border-stone-300"
                key={entry.label}
                to={entry.href}
              >
                <p className="text-2xl font-semibold tracking-tight">
                  {entry.value}
                </p>
                <p className="mt-1 text-xs font-semibold text-stone-600">
                  {entry.label}
                </p>
              </Link>
            ))}
          </div>
        </Disclosure>
      ) : null}
    </div>
  );
}
